import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography,
    CircularProgress, 
    TextField, 
    Button,
    FormControlLabel, 
    Checkbox,
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const CrewScheduleCodePage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const { crewScheduleCodeId } = useParams();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [crewScheduleCodeCategories, setCrewScheduleCodeCategories] = useState([]);
    const [formValues, setFormValues] = useState({
        code: '',
        description: '',
        category: '',
        isTimesheet: false,
        workingDays: 0,
        isPaid: false,
        isCrewRequest: false,
    });


    const fetchCrewScheduleCodeCategories = useCallback(async () => {
        return await api.get('/crewschedulecodecategories');
    }, []);
    
    const fetchCrewScheduleCode = useCallback(async () => {
        if (crewScheduleCodeId) {
            const url = `/crewschedulecodes/${crewScheduleCodeId}`;
            return await api.get(url);            
        }
    }, [crewScheduleCodeId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [
                    crewScheduleCodeCategoriesResponse,
                    CrewScheduleCodesResponse,
                ] = await Promise.all([
                    fetchCrewScheduleCodeCategories(),
                    crewScheduleCodeId ? fetchCrewScheduleCode() : Promise.resolve(null),
                ]);
                
                setCrewScheduleCodeCategories(crewScheduleCodeCategoriesResponse.data);        
                
                const formData = CrewScheduleCodesResponse?.data || {};             
                setFormValues({
                    code: formData.code || '',
                    description: formData.description || '',
                    category: formData.category || '',
                    isTimesheet: formData.isTimesheet ?? false,
                    workingDays: formData.workingDays ?? 0,
                    isPaid: formData.isPaid ?? false,
                    isCrewRequest: formData.isCrewRequest ?? false,                    
                });                
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [
        fetchCrewScheduleCodeCategories,
        fetchCrewScheduleCode,
        crewScheduleCodeId,
    ]); 
    
    const checkoutSchema = yup.object().shape({
        code: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values) => {
        try {
            const method = crewScheduleCodeId ? 'PUT' : 'POST';
            const url = crewScheduleCodeId
                ? `/crewschedulecodes/update/${crewScheduleCodeId}`
                : '/crewschedulecodes/new';

            const response = await api({ method, url, data: values });

            if (![200, 201].includes(response.status)) {
                toast.error('Failed to submit the form.');
                return;
            }

            toast.success(crewScheduleCodeId
                ? 'Crew schedule code updated successfully.'
                : 'Crew schedule code created successfully.'
            );
            navigate("/crewschedulecodes");
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || 'Failed to submit the form.');
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Crew Schedule Code"
                    subtitle={loading ? 'Loading...' : crewScheduleCodeId ? 'Update' : 'Create'}
                />
                {!loading && (
                    <StyledButton onClick={() => navigate("/crewschedulecodes")}>
                        <ArrowBackIosNewIcon />
                    </StyledButton>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <Typography color="error">Error: {error?.message || ''}</Typography>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                >
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        required
                                        label="Code"
                                        name="code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.code}
                                        error={!!touched.code && !!errors.code}
                                        helperText={touched.code && errors.code}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        label="Description"
                                        name="description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <FormControl
                                        fullWidth
                                        variant="filled"
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        error={!!touched.category && !!errors.category}
                                    >
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            name="category"
                                            value={values.category}
                                            onBlur={handleBlur}
                                            onChange={(e) => setFieldValue('category', e.target.value)}
                                        >
                                            <MenuItem value="">--- None ---</MenuItem>
                                            {crewScheduleCodeCategories?.map((cat) => (
                                                <MenuItem 
                                                    key={cat._id} 
                                                    value={cat.categoryCode}
                                                >
                                                    {cat.categoryCode}{cat.description ? ` - ${cat.description}` : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Working Days"
                                        name="workingDays"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.workingDays}
                                        error={!!touched.workingDays && !!errors.workingDays}
                                        helperText={touched.workingDays && errors.workingDays}
                                        slotProps={{
                                            input: { sx: { "& input": { textAlign: "right" } } },
                                        }}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                                        control={
                                            <Checkbox
                                                name="isTimesheet"
                                                checked={values.isTimesheet}
                                                onChange={handleChange}
                                                color="primary"
                                            />
                                        }
                                        label="Is Timesheet"
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                                        control={
                                            <Checkbox
                                                name="isPaid"
                                                checked={values.isPaid}
                                                onChange={handleChange}
                                                color="primary"
                                            />
                                        }
                                        label="Is Paid"
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                                        control={
                                            <Checkbox
                                                name="isCrewRequest"
                                                checked={values.isCrewRequest}
                                                onChange={handleChange}
                                                color="primary"
                                            />
                                        }
                                        label="Is Crew Request"
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {crewScheduleCodeId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default CrewScheduleCodePage;
